import React, { useState } from 'react';
import { Upload, Brain, Shield, Bell, FileSearch, Scale, CheckCircle, ArrowRight, Mic, MessageSquare, Building2, FileText, AlertTriangle, Calendar, Lock, Globe, ChevronRight } from 'lucide-react';
type FeatureCardProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  highlights: string[];
};
function FeatureCard({
  icon,
  title,
  description,
  highlights
}: FeatureCardProps) {
  return <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-lg transition-all duration-300" data-id="element-2830">
      <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4" data-id="element-2831">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2832">{title}</h3>
      <p className="text-gray-600 text-sm mb-4" data-id="element-2833">{description}</p>
      <ul className="space-y-2" data-id="element-2834">
        {highlights.map((highlight, index) => <li key={index} className="flex items-start text-sm text-gray-700" data-id="element-2835">
            <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2836" />
            {highlight}
          </li>)}
      </ul>
    </div>;
}
type DocumentCategoryProps = {
  icon: React.ReactNode;
  title: string;
  examples: string[];
  color: string;
};
function DocumentCategory({
  icon,
  title,
  examples,
  color
}: DocumentCategoryProps) {
  return <div className={`${color} rounded-lg p-4`} data-id="element-2837">
      <div className="flex items-center mb-3" data-id="element-2838">
        {icon}
        <span className="font-medium text-gray-800 ml-2" data-id="element-2839">{title}</span>
      </div>
      <div className="flex flex-wrap gap-2" data-id="element-2840">
        {examples.map((example, index) => <span key={index} className="text-xs bg-white/60 text-gray-700 px-2 py-1 rounded" data-id="element-2841">
            {example}
          </span>)}
      </div>
    </div>;
}
export function LegalOS() {
  const [activeTab, setActiveTab] = useState<'upload' | 'analyze' | 'insights'>('upload');
  const features: FeatureCardProps[] = [{
    icon: <Brain className="w-6 h-6 text-blue-600" data-id="element-2842" />,
    title: 'AI Document Analysis',
    description: 'Our AI scans and analyzes every document you upload, extracting key information automatically.',
    highlights: ['Automatic data extraction', 'Smart categorization', 'Key terms identification', 'Risk assessment']
  }, {
    icon: <MessageSquare className="w-6 h-6 text-blue-600" data-id="element-2843" />,
    title: 'Intelligent Q&A',
    description: 'Ask questions about your documents using text or voice and get instant, accurate answers.',
    highlights: ['Natural language queries', 'Voice command support', 'Cross-document search', 'Citation references']
  }, {
    icon: <Shield className="w-6 h-6 text-blue-600" data-id="element-2844" />,
    title: 'Compliance Monitoring',
    description: 'Identify compliance gaps specific to your industry and jurisdiction automatically.',
    highlights: ['Industry-specific rules', 'Gap identification', 'Remediation guidance', 'Audit-ready reports']
  }, {
    icon: <Bell className="w-6 h-6 text-blue-600" data-id="element-2845" />,
    title: 'Smart Notifications',
    description: 'Never miss a deadline with automated alerts for payments, renewals, and expirations.',
    highlights: ['Payment deadlines', 'Contract expirations', 'Renewal reminders', 'Custom alert rules']
  }, {
    icon: <Scale className="w-6 h-6 text-blue-600" data-id="element-2846" />,
    title: 'Liability Analysis',
    description: 'Understand your exposure across all documents with comprehensive liability reports.',
    highlights: ['Risk quantification', 'Exposure mapping', 'Trend analysis', 'Mitigation suggestions']
  }, {
    icon: <Lock className="w-6 h-6 text-blue-600" data-id="element-2847" />,
    title: 'Enterprise Security',
    description: 'Your data is protected with bank-level encryption and full regulatory compliance.',
    highlights: ['SOC 2 Type II certified', 'PIPEDA compliant', 'CCPA compliant', '256-bit encryption']
  }];
  const documentCategories: DocumentCategoryProps[] = [{
    icon: <FileText className="w-5 h-5 text-blue-600" data-id="element-2848" />,
    title: 'Compliance Documents',
    examples: ['Licenses', 'Permits', 'Certifications', 'Registrations'],
    color: 'bg-blue-50'
  }, {
    icon: <Building2 className="w-5 h-5 text-purple-600" data-id="element-2849" />,
    title: 'Employee Contracts',
    examples: ['Employment Agreements', 'NDAs', 'Non-Competes', 'Offer Letters'],
    color: 'bg-purple-50'
  }, {
    icon: <FileSearch className="w-5 h-5 text-green-600" data-id="element-2850" />,
    title: 'Third Party Contracts',
    examples: ['Vendor Agreements', 'Service Contracts', 'SLAs', 'MSAs'],
    color: 'bg-green-50'
  }, {
    icon: <Shield className="w-5 h-5 text-amber-600" data-id="element-2851" />,
    title: 'Insurance Policies',
    examples: ['General Liability', 'E&O', 'D&O', 'Workers Comp'],
    color: 'bg-amber-50'
  }];
  return <div className="min-h-screen bg-slate-50" data-id="element-2852">
      {/* Header */}
      <header className="bg-[rgb(16,22,51)] text-white" data-id="element-2853">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-2854">
          <div className="flex items-center" data-id="element-2855">
            <img src="https://app.lawvo.com/static/media/LawVo.6153e5c6efe17719c66ad7b40b37a848.svg" alt="LawVo Logo" className="h-8" data-id="element-2856" />
            <span className="ml-3 text-lg font-semibold text-blue-400" data-id="element-2857">
              LegalOS
            </span>
          </div>
          <nav className="hidden md:flex items-center space-x-8" data-id="element-2858">
            <a href="#features" className="text-gray-300 hover:text-white transition-colors" data-id="element-2859">
              Features
            </a>
            <a href="#how-it-works" className="text-gray-300 hover:text-white transition-colors" data-id="element-2860">
              How It Works
            </a>
            <a href="#security" className="text-gray-300 hover:text-white transition-colors" data-id="element-2861">
              Security
            </a>
            <button className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg font-medium transition-colors" data-id="element-2862">
              Get Started
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[rgb(16,22,51)] to-slate-900 text-white py-20" data-id="element-2863">
        <div className="max-w-7xl mx-auto px-6" data-id="element-2864">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-2865">
            <div data-id="element-2866">
              <div className="inline-flex items-center bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full text-sm mb-6" data-id="element-2867">
                <Globe className="w-4 h-4 mr-2" data-id="element-2868" />
                Serving SMBs across Canada & USA
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight" data-id="element-2869">
                Your AI-Powered
                <span className="text-blue-400" data-id="element-2870"> Legal Command Center</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed" data-id="element-2871">
                One secure portal for all your business legal documents. Upload,
                analyze, and get instant answers with AI that understands your
                entire document library.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-2872">
                <button className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-semibold flex items-center justify-center transition-colors" data-id="element-2873">
                  Start Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" data-id="element-2874" />
                </button>
                <button className="border border-gray-600 hover:border-gray-500 px-6 py-3 rounded-lg font-semibold flex items-center justify-center transition-colors" data-id="element-2875">
                  Watch Demo
                </button>
              </div>
              <div className="mt-8 flex items-center space-x-6 text-sm text-gray-400" data-id="element-2876">
                <div className="flex items-center" data-id="element-2877">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" data-id="element-2878" />
                  No credit card required
                </div>
                <div className="flex items-center" data-id="element-2879">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" data-id="element-2880" />
                  14-day free trial
                </div>
              </div>
            </div>

            {/* Interactive Demo Preview */}
            <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700" data-id="element-2881">
              <div className="flex items-center space-x-2 mb-4" data-id="element-2882">
                <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-2883"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-2884"></div>
                <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-2885"></div>
              </div>

              {/* Tab Navigation */}
              <div className="flex space-x-1 bg-slate-700/50 rounded-lg p-1 mb-4" data-id="element-2886">
                {(['upload', 'analyze', 'insights'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${activeTab === tab ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`} data-id="element-2887">
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>)}
              </div>

              {/* Tab Content */}
              <div className="bg-slate-900 rounded-lg p-4 min-h-[200px]" data-id="element-2888">
                {activeTab === 'upload' && <div className="text-center py-8" data-id="element-2889">
                    <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2890">
                      <Upload className="w-8 h-8 text-blue-400" data-id="element-2891" />
                    </div>
                    <p className="text-gray-300 mb-2" data-id="element-2892">
                      Drag & drop your documents
                    </p>
                    <p className="text-gray-500 text-sm" data-id="element-2893">
                      PDF, DOCX, images supported
                    </p>
                  </div>}
                {activeTab === 'analyze' && <div className="space-y-3" data-id="element-2894">
                    <div className="flex items-center justify-between bg-slate-800 p-3 rounded-lg" data-id="element-2895">
                      <div className="flex items-center" data-id="element-2896">
                        <FileText className="w-5 h-5 text-blue-400 mr-3" data-id="element-2897" />
                        <span className="text-gray-300 text-sm" data-id="element-2898">
                          Employment_Contract.pdf
                        </span>
                      </div>
                      <span className="text-green-400 text-xs" data-id="element-2899">Analyzed</span>
                    </div>
                    <div className="flex items-center justify-between bg-slate-800 p-3 rounded-lg" data-id="element-2900">
                      <div className="flex items-center" data-id="element-2901">
                        <FileText className="w-5 h-5 text-purple-400 mr-3" data-id="element-2902" />
                        <span className="text-gray-300 text-sm" data-id="element-2903">
                          Vendor_Agreement.pdf
                        </span>
                      </div>
                      <span className="text-yellow-400 text-xs" data-id="element-2904">
                        Processing...
                      </span>
                    </div>
                    <div className="flex items-center justify-between bg-slate-800 p-3 rounded-lg" data-id="element-2905">
                      <div className="flex items-center" data-id="element-2906">
                        <FileText className="w-5 h-5 text-green-400 mr-3" data-id="element-2907" />
                        <span className="text-gray-300 text-sm" data-id="element-2908">
                          Insurance_Policy.pdf
                        </span>
                      </div>
                      <span className="text-green-400 text-xs" data-id="element-2909">Analyzed</span>
                    </div>
                  </div>}
                {activeTab === 'insights' && <div className="space-y-3" data-id="element-2910">
                    <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-lg" data-id="element-2911">
                      <div className="flex items-center text-amber-400 text-sm mb-1" data-id="element-2912">
                        <AlertTriangle className="w-4 h-4 mr-2" data-id="element-2913" />
                        Upcoming Deadline
                      </div>
                      <p className="text-gray-300 text-sm" data-id="element-2914">
                        Insurance renewal due in 14 days
                      </p>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 p-3 rounded-lg" data-id="element-2915">
                      <div className="flex items-center text-red-400 text-sm mb-1" data-id="element-2916">
                        <Shield className="w-4 h-4 mr-2" data-id="element-2917" />
                        Compliance Gap
                      </div>
                      <p className="text-gray-300 text-sm" data-id="element-2918">
                        Missing OSHA certification
                      </p>
                    </div>
                    <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-lg" data-id="element-2919">
                      <div className="flex items-center text-blue-400 text-sm mb-1" data-id="element-2920">
                        <Scale className="w-4 h-4 mr-2" data-id="element-2921" />
                        Liability Alert
                      </div>
                      <p className="text-gray-300 text-sm" data-id="element-2922">
                        3 contracts with unlimited liability clauses
                      </p>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Document Categories */}
      <section className="py-16 bg-white" data-id="element-2923">
        <div className="max-w-7xl mx-auto px-6" data-id="element-2924">
          <div className="text-center mb-12" data-id="element-2925">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2926">
              One Place for All Your Legal Documents
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-2927">
              Upload any type of business document. Our AI understands them all.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2928">
            {documentCategories.map((category, index) => <DocumentCategory key={index} {...category} data-id="element-2929" />)}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 bg-slate-50" data-id="element-2930">
        <div className="max-w-7xl mx-auto px-6" data-id="element-2931">
          <div className="text-center mb-16" data-id="element-2932">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2933">
              Powerful Features for Modern Businesses
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-2934">
              Everything you need to manage, understand, and stay on top of your
              legal obligations.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2935">
            {features.map((feature, index) => <FeatureCard key={index} {...feature} data-id="element-2936" />)}
          </div>
        </div>
      </section>

      {/* AI Assistant Section */}
      <section id="how-it-works" className="py-20 bg-white" data-id="element-2937">
        <div className="max-w-7xl mx-auto px-6" data-id="element-2938">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-2939">
            <div data-id="element-2940">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-2941">
                Ask Anything About Your Documents
              </h2>
              <p className="text-gray-600 mb-8" data-id="element-2942">
                Our AI assistant reads and understands every document you
                upload. Ask questions in plain English or use voice commands to
                get instant answers with references.
              </p>

              <div className="space-y-4" data-id="element-2943">
                <div className="flex items-start" data-id="element-2944">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-2945">
                    <MessageSquare className="w-5 h-5 text-blue-600" data-id="element-2946" />
                  </div>
                  <div data-id="element-2947">
                    <h4 className="font-semibold text-gray-900 mb-1" data-id="element-2948">
                      Text Queries
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-2949">
                      Type natural language questions and get instant answers
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-2950">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-2951">
                    <Mic className="w-5 h-5 text-purple-600" data-id="element-2952" />
                  </div>
                  <div data-id="element-2953">
                    <h4 className="font-semibold text-gray-900 mb-1" data-id="element-2954">
                      Voice Commands
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-2955">
                      Speak your questions for hands-free document search
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-2956">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-2957">
                    <FileSearch className="w-5 h-5 text-green-600" data-id="element-2958" />
                  </div>
                  <div data-id="element-2959">
                    <h4 className="font-semibold text-gray-900 mb-1" data-id="element-2960">
                      Cross-Document Search
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-2961">
                      Find information across all your documents at once
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Chat Demo */}
            <div className="bg-slate-100 rounded-2xl p-6" data-id="element-2962">
              <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-id="element-2963">
                <div className="bg-slate-800 text-white px-4 py-3 flex items-center" data-id="element-2964">
                  <Brain className="w-5 h-5 text-blue-400 mr-2" data-id="element-2965" />
                  <span className="font-medium" data-id="element-2966">LegalOS Assistant</span>
                </div>
                <div className="p-4 space-y-4 min-h-[300px]" data-id="element-2967">
                  <div className="flex justify-end" data-id="element-2968">
                    <div className="bg-blue-600 text-white px-4 py-2 rounded-2xl rounded-br-sm max-w-[80%]" data-id="element-2969">
                      <p className="text-sm" data-id="element-2970">
                        When does our liability insurance expire?
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-start" data-id="element-2971">
                    <div className="bg-slate-100 px-4 py-3 rounded-2xl rounded-bl-sm max-w-[80%]" data-id="element-2972">
                      <p className="text-sm text-gray-800 mb-2" data-id="element-2973">
                        Based on your uploaded documents, your General Liability
                        Insurance policy expires on{' '}
                        <strong data-id="element-2974">March 15, 2025</strong>.
                      </p>
                      <div className="text-xs text-gray-500 flex items-center" data-id="element-2975">
                        <FileText className="w-3 h-3 mr-1" data-id="element-2976" />
                        Source: GL_Insurance_Policy_2024.pdf
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end" data-id="element-2977">
                    <div className="bg-blue-600 text-white px-4 py-2 rounded-2xl rounded-br-sm max-w-[80%]" data-id="element-2978">
                      <p className="text-sm" data-id="element-2979">Set a reminder 30 days before</p>
                    </div>
                  </div>
                  <div className="flex justify-start" data-id="element-2980">
                    <div className="bg-slate-100 px-4 py-3 rounded-2xl rounded-bl-sm max-w-[80%]" data-id="element-2981">
                      <p className="text-sm text-gray-800 flex items-center" data-id="element-2982">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" data-id="element-2983" />
                        Reminder set for February 13, 2025
                      </p>
                    </div>
                  </div>
                </div>
                <div className="border-t px-4 py-3 flex items-center" data-id="element-2984">
                  <input type="text" placeholder="Ask about your documents..." className="flex-1 bg-slate-50 border-0 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2985" />
                  <button className="ml-2 p-2 text-gray-400 hover:text-blue-600 transition-colors" data-id="element-2986">
                    <Mic className="w-5 h-5" data-id="element-2987" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security & Compliance */}
      <section id="security" className="py-20 bg-slate-900 text-white" data-id="element-2988">
        <div className="max-w-7xl mx-auto px-6" data-id="element-2989">
          <div className="text-center mb-16" data-id="element-2990">
            <h2 className="text-3xl font-bold mb-4" data-id="element-2991">
              Enterprise-Grade Security & Compliance
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-2992">
              Your sensitive legal documents deserve the highest level of
              protection. We comply with all Canadian and US data protection
              regulations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2993">
            <div className="bg-slate-800 rounded-xl p-6 text-center" data-id="element-2994">
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2995">
                <Lock className="w-6 h-6 text-blue-400" data-id="element-2996" />
              </div>
              <h4 className="font-semibold mb-2" data-id="element-2997">256-bit Encryption</h4>
              <p className="text-gray-400 text-sm" data-id="element-2998">
                Bank-level encryption for data at rest and in transit
              </p>
            </div>
            <div className="bg-slate-800 rounded-xl p-6 text-center" data-id="element-2999">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3000">
                <Shield className="w-6 h-6 text-green-400" data-id="element-3001" />
              </div>
              <h4 className="font-semibold mb-2" data-id="element-3002">PIPEDA Compliant</h4>
              <p className="text-gray-400 text-sm" data-id="element-3003">
                Full compliance with Canadian privacy laws
              </p>
            </div>
            <div className="bg-slate-800 rounded-xl p-6 text-center" data-id="element-3004">
              <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3005">
                <Globe className="w-6 h-6 text-purple-400" data-id="element-3006" />
              </div>
              <h4 className="font-semibold mb-2" data-id="element-3007">CCPA Compliant</h4>
              <p className="text-gray-400 text-sm" data-id="element-3008">
                Meeting California Consumer Privacy Act requirements
              </p>
            </div>
            <div className="bg-slate-800 rounded-xl p-6 text-center" data-id="element-3009">
              <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3010">
                <CheckCircle className="w-6 h-6 text-amber-400" data-id="element-3011" />
              </div>
              <h4 className="font-semibold mb-2" data-id="element-3012">SOC 2 Type II</h4>
              <p className="text-gray-400 text-sm" data-id="element-3013">
                Audited security controls and processes
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white" data-id="element-3014">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-3015">
          <h2 className="text-3xl font-bold mb-4" data-id="element-3016">
            Ready to Take Control of Your Legal Documents?
          </h2>
          <p className="text-blue-100 mb-8 text-lg" data-id="element-3017">
            Join thousands of SMBs across Canada and the US who trust LegalOS to
            manage their legal compliance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-3018">
            <button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-3 rounded-lg font-semibold flex items-center justify-center transition-colors" data-id="element-3019">
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-3020" />
            </button>
            <button className="border-2 border-white hover:bg-white/10 px-8 py-3 rounded-lg font-semibold flex items-center justify-center transition-colors" data-id="element-3021">
              Schedule Demo
              <Calendar className="w-5 h-5 ml-2" data-id="element-3022" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-gray-400 py-12" data-id="element-3023">
        <div className="max-w-7xl mx-auto px-6" data-id="element-3024">
          <div className="flex flex-col md:flex-row justify-between items-center" data-id="element-3025">
            <div className="flex items-center mb-4 md:mb-0" data-id="element-3026">
              <img src="https://app.lawvo.com/static/media/LawVo.6153e5c6efe17719c66ad7b40b37a848.svg" alt="LawVo Logo" className="h-6" data-id="element-3027" />
              <span className="ml-2 text-blue-400 font-medium" data-id="element-3028">LegalOS</span>
            </div>
            <div className="flex items-center space-x-6 text-sm" data-id="element-3029">
              <a href="#" className="hover:text-white transition-colors" data-id="element-3030">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-white transition-colors" data-id="element-3031">
                Terms of Service
              </a>
              <a href="#" className="hover:text-white transition-colors" data-id="element-3032">
                Contact
              </a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-sm" data-id="element-3033">
            <p data-id="element-3034">
              © 2024 LawVo. All rights reserved. Serving businesses in Canada
              and the United States.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}